<template>
  <Alert :variant="VARIANT[tone]" :role="role" class="items-center" data-slot="notice" :data-tone="tone">
    <component :is="ICON[tone]" v-if="icon" aria-hidden="true" />
    <div class="col-start-2 flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
      <div class="min-w-0">
        <AlertTitle class="font-semibold">
          <slot name="title">{{ title }}</slot>
        </AlertTitle>
        <AlertDescription v-if="$slots.default || description">
          <slot>{{ description }}</slot>
        </AlertDescription>
      </div>
      <!-- The auto margin keeps the action at the right when a long message
           wraps it onto a line of its own. -->
      <div v-if="$slots.action" class="ml-auto shrink-0"><slot name="action" /></div>
    </div>
  </Alert>
</template>

<script setup lang="ts">
// A message about the page: one bold sentence, one muted line, at most one action.
// It sits between the header and the tabs (AdminPage's #notice) or above data a
// reload failed to refresh, and replaces every hand-drawn banner box. A block
// that failed with nothing to show is a LoadError instead. The tone
// picks the colour and the icon; the role follows Alert's rule (only a failure
// announces itself), so a standing notice does not interrupt a screen reader.
// A one-line notice inside a card can drop the icon: the box already says it
// is a notice, and the icon costs the line its width.
import { CircleAlertIcon, InfoIcon, TriangleAlertIcon } from "lucide-vue-next";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";

// Neutral is a box with no colour: a standing fact about the thing it sits
// in, with an action, where a tint would read as something gone wrong.
export type NoticeTone = "warn" | "bad" | "info" | "neutral";

withDefaults(defineProps<{ tone: NoticeTone; title?: string; description?: string; role?: string; icon?: boolean }>(), {
  title: "",
  description: "",
  role: undefined,
  icon: true,
});

const VARIANT = { warn: "warning", bad: "destructive", info: "info", neutral: "default" } as const;
const ICON = { warn: TriangleAlertIcon, bad: CircleAlertIcon, info: InfoIcon, neutral: InfoIcon };
</script>
